/**
 * EmailChangeVerification Component
 *
 * Handles email address change verification via token from email link.
 * Follows the same pattern as AccountActivation.tsx for consistency.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import {
  Card,
  CardBody,
  Button,
  Alert
} from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';
import { useAuth } from '@/hooks/useAuth';

/**
 * Email Change Verification Component
 *
 * Validates email change token and updates user's email address.
 * Shows loading, success, or error states based on verification result.
 */
export const EmailChangeVerification: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { verifyEmailChange, isAuthenticated } = useAuth();

  // Component state
  const [verificationState, setVerificationState] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Verify the email change token
  const verifyToken = useCallback(async () => {
    if (!token) {
      setVerificationState('error');
      setErrorMessage('No verification token provided');
      return;
    }

    // Check if this token was already successfully verified (survives page reload)
    const verificationKey = `email_verification_${token}`;
    const previousResult = sessionStorage.getItem(verificationKey);

    if (previousResult) {
      const { newEmail: storedEmail } = JSON.parse(previousResult);
      setVerificationState('success');
      setNewEmail(storedEmail);
      return;
    }

    // Prevent multiple simultaneous calls
    if (isVerifying) {
      return;
    }

    try {
      setIsVerifying(true);
      setVerificationState('loading');

      const result = await verifyEmailChange({ verificationToken: token });

      if (result.success) {
        const email = (result.data?.['newEmail'] as string) || 'Updated email address';
        setVerificationState('success');
        setNewEmail(email);

        // Store success in sessionStorage to survive page reloads
        sessionStorage.setItem(verificationKey, JSON.stringify({ newEmail: email }));

        // Don't auto-redirect - let user manually continue when ready
      } else {
        setVerificationState('error');
        setErrorMessage(result.message || 'Email verification failed');
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setVerificationState('error');
      setErrorMessage('An unexpected error occurred while verifying your email change');
    } finally {
      setIsVerifying(false);
    }
  }, [token, verifyEmailChange, isVerifying]);

  // Verify token on component mount
  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Handle retry verification
  const handleRetry = useCallback(() => {
    verifyToken();
  }, [verifyToken]);

  // Handle manual navigation to profile
  const handleGoToProfile = useCallback(() => {
    // Clean up sessionStorage before navigating
    if (token) {
      sessionStorage.removeItem(`email_verification_${token}`);
    }
    navigate('/settings/profile', { replace: true });
  }, [navigate, token]);

  // Handle requesting new verification
  const handleRequestNewVerification = useCallback(() => {
    navigate('/settings/profile', { replace: true });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card shadow="lg" className="border border-border-300">
          <CardBody className="p-8 text-center">
            {verificationState === 'loading' && (
              <>
                <div className="flex justify-center mb-6">
                  <Loader2 className="w-16 h-16 text-primary animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-text-primary mb-4">
                  Verifying Email Change
                </h1>
                <p className="text-text-secondary mb-6">
                  Please wait while we verify your email address change...
                </p>
              </>
            )}

            {verificationState === 'success' && (
              <>
                <div className="flex justify-center mb-6">
                  <CheckCircle className="w-16 h-16 text-success-600" />
                </div>
                <h1 className="text-2xl font-bold text-text-primary mb-4">
                  Email Address Updated!
                </h1>
                <p className="text-text-secondary mb-2">
                  Your email address has been successfully changed to:
                </p>
                <p className="text-lg font-medium text-primary mb-6">
                  {newEmail}
                </p>
                <p className="text-sm text-text-secondary mb-6">
                  {isAuthenticated
                    ? 'You can now continue to your profile or close this page.'
                    : 'You can now log in with your new email address.'}
                </p>
                <Button
                  color="primary"
                  size="lg"
                  onPress={isAuthenticated ? handleGoToProfile : () => {
                    // Clean up sessionStorage before navigating to login
                    if (token) {
                      sessionStorage.removeItem(`email_verification_${token}`);
                    }
                    navigate('/login');
                  }}
                  startContent={<Mail size={20} />}
                  className="w-full"
                >
                  {isAuthenticated ? 'Go to Profile' : 'Login with New Email'}
                </Button>
              </>
            )}

            {verificationState === 'error' && (
              <>
                <div className="flex justify-center mb-6">
                  <XCircle className="w-16 h-16 text-danger-600" />
                </div>
                <h1 className="text-2xl font-bold text-text-primary mb-4">
                  Verification Failed
                </h1>

                <Alert color="danger" variant="flat" className="mb-6 text-left">
                  {errorMessage}
                </Alert>

                <div className="space-y-3">
                  <Button
                    color="primary"
                    variant="bordered"
                    size="lg"
                    onPress={handleRetry}
                    className="w-full"
                  >
                    Try Again
                  </Button>

                  <Button
                    color="default"
                    variant="light"
                    size="lg"
                    onPress={handleRequestNewVerification}
                    className="w-full"
                  >
                    Request New Verification
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-surface-100 rounded-lg">
                  <h3 className="text-sm font-medium text-text-primary mb-2">
                    Common issues:
                  </h3>
                  <ul className="text-xs text-text-secondary space-y-1 text-left">
                    <li>• Verification link may have expired (links expire after 1 hour)</li>
                    <li>• Link may have already been used</li>
                    <li>• Check that you clicked the full link from your email</li>
                  </ul>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-text-secondary">
            Need help? Contact{' '}
            <a
              href={`mailto:${Brand.supportEmail}`}
              className="text-primary hover:underline"
            >
              {Brand.supportEmail}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};